import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { useSearchParams } from 'react-router'
import {
  Banner,
  Button,
  Container,
  Heading,
  HStack,
  VStack,
  useIsMobile,
} from 'wss3-forge'
import {
  Brain20Regular,
  Calendar20Regular,
  PlugConnected20Regular,
} from '@fluentui/react-icons'
import { ChatComposer } from '../components/ChatComposer.js'
import type { Attachment } from '../lib/api.js'
import { MessageList } from '../components/MessageList.js'
import { VoiceModeOverlay } from '../components/VoiceModeOverlay.js'
import { useAuth } from '../contexts/AuthContext.js'
import { useIncognito } from '../contexts/IncognitoContext.js'
import { useChatContext } from '../contexts/ChatContext.js'
import { useSpeechSynthesis } from '../hooks/useSpeechSynthesis.js'
import { WAKE_EVENT } from '../hooks/useRealtimeEvents.js'

const TTS_STORAGE_KEY = 'stark.tts.enabled'

interface Suggestion {
  label: string
  prompt: string
  icon: ReactNode
}

const SUGGESTIONS: Suggestion[] = [
  {
    label: 'Mes rendez-vous demain',
    prompt: 'Quels sont mes rendez-vous Calendly et Google Calendar pour demain ?',
    icon: <Calendar20Regular />,
  },
  {
    label: 'Crée une issue GitHub',
    prompt: "Crée une issue sur webba/stark intitulée 'Bug à investiguer'",
    icon: <PlugConnected20Regular />,
  },
  {
    label: 'Mes mémoires récentes',
    prompt: 'Quelles sont les 5 mémoires les plus récentes que tu as de moi ?',
    icon: <Brain20Regular />,
  },
]

/**
 * Time-of-day greeting. We round the buckets to the way people actually
 * talk: "bonjour" until noon, "bon après-midi" until 18h, "bonsoir" until
 * 23h, "bonne nuit" otherwise.
 */
function greetingFor(hour: number): string {
  if (hour < 6) return 'Bonne nuit'
  if (hour < 12) return 'Bonjour'
  if (hour < 18) return 'Bon après-midi'
  if (hour < 23) return 'Bonsoir'
  return 'Bonne nuit'
}

/** Rotated daily so the welcome screen feels alive without being random. */
const SUBTITLES = [
  'Je suis dispo, on attaque par quoi ?',
  'À ton écoute.',
  'Prêt quand tu veux.',
  'Que puis-je faire pour toi ?',
  'On bosse sur quoi ?',
  'Dis-moi tout.',
  'Je t’écoute.',
]

function capitalize(s: string): string {
  if (!s) return s
  return s.charAt(0).toUpperCase() + s.slice(1)
}

function loadTtsEnabled(): boolean {
  if (typeof window === 'undefined') return true
  return window.localStorage.getItem(TTS_STORAGE_KEY) !== 'false'
}

function firstNameOf(fullName: string | undefined): string {
  if (!fullName) return ''
  const trimmed = fullName.trim()
  const space = trimmed.indexOf(' ')
  return space === -1 ? trimmed : trimmed.slice(0, space)
}

export function ChatPage() {
  const { messages, sessionId, isSending, error, activities, send, clearError } = useChatContext()
  const { user } = useAuth()
  const { incognito } = useIncognito()
  const isMobile = useIsMobile()
  const tts = useSpeechSynthesis()

  const [ttsEnabled] = useState<boolean>(loadTtsEnabled)
  const [voiceModeOpen, setVoiceModeOpen] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()

  // ?voice=1 in the URL = bridge wake-word triggered while we were on another
  // page. Open the voice overlay and clear the param so a refresh doesn't
  // re-trigger.
  useEffect(() => {
    if (searchParams.get('voice') === '1') {
      setVoiceModeOpen(true)
      const next = new URLSearchParams(searchParams)
      next.delete('voice')
      setSearchParams(next, { replace: true })
    }
  }, [searchParams, setSearchParams])

  // Wake-word fired while we ARE already on /chat - open the overlay directly.
  useEffect(() => {
    const onWake = (): void => setVoiceModeOpen(true)
    window.addEventListener(WAKE_EVENT, onWake)
    return () => window.removeEventListener(WAKE_EVENT, onWake)
  }, [])

  // Track which assistant message index we have already spoken so refreshes,
  // re-renders, or session restores don't replay old messages.
  const lastSpokenIndexRef = useRef<number>(messages.length - 1)

  useEffect(() => {
    if (!ttsEnabled || !tts.isSupported) return
    const lastIndex = messages.length - 1
    if (lastIndex <= lastSpokenIndexRef.current) return
    const last = messages[lastIndex]
    if (!last || last.role !== 'assistant') {
      lastSpokenIndexRef.current = lastIndex
      return
    }
    lastSpokenIndexRef.current = lastIndex
    tts.speak(last.content)
  }, [messages, ttsEnabled, tts])

  const handleSend = useCallback(
    (message: string, attachments?: Attachment[]) => {
      tts.cancel()
      send(message, attachments)
    },
    [send, tts],
  )

  const openVoiceMode = useCallback(() => {
    tts.cancel()
    setVoiceModeOpen(true)
  }, [tts])

  const isWelcome = messages.length === 0 && !isSending

  if (isWelcome) {
    const firstName = capitalize(firstNameOf(user?.name))
    const now = new Date()
    const greeting = greetingFor(now.getHours())
    const subtitle = SUBTITLES[now.getDate() % SUBTITLES.length]!
    const placeholder = incognito
      ? 'Mode sécurisé Webba, rien n’est envoyé en ligne...'
      : "Comment puis-je t'aider ?"
    return (
      <Container
        maxWidth="md"
        py="xl"
        style={{
          minHeight: isMobile ? 'calc(100dvh - 80px - 56px)' : '100dvh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <VStack gap="xl" align="center" fullWidth>
          <VStack gap="xs" align="center">
            <Heading level={1} size="4xl" weight="semibold" align="center">
              {firstName ? `${greeting} ${firstName}` : greeting}
            </Heading>
            <Heading level={3} size="lg" weight="normal" color="muted" align="center">
              {subtitle}
            </Heading>
          </VStack>

          <div style={{ width: '100%', maxWidth: 720 }}>
            <ChatComposer
              disabled={isSending}
              onSend={handleSend}
              onVoiceMode={openVoiceMode}
              placeholder={placeholder}
            />
          </div>

          <HStack
            gap="xs"
            wrap
            justify="center"
            style={{ maxWidth: 720, width: '100%' }}
          >
            {SUGGESTIONS.map((s) => (
              <Button
                key={s.label}
                variant="outline"
                size="sm"
                icon={s.icon}
                onClick={() => handleSend(s.prompt)}
              >
                {s.label}
              </Button>
            ))}
          </HStack>

          {error && (
            <div style={{ width: '100%', maxWidth: 720 }}>
              <Banner variant="error" title="Échec de l'envoi" showCloseButton onClose={clearError}>
                {error}
              </Banner>
            </div>
          )}
        </VStack>

        <VoiceModeOverlay
          open={voiceModeOpen}
          sessionId={sessionId}
          onClose={() => setVoiceModeOpen(false)}
        />
      </Container>
    )
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: isMobile ? 'calc(100dvh - 80px - 56px)' : '100dvh',
        minHeight: 0,
      }}
    >
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <MessageList
          messages={messages}
          isThinking={isSending}
          activities={activities}
        />
      </div>

      {error && (
        <div style={{ padding: 'var(--spacing-sm) var(--spacing-lg) 0' }}>
          <div style={{ maxWidth: 880, marginInline: 'auto', width: '100%' }}>
            <Banner variant="error" title="Échec de l'envoi" showCloseButton onClose={clearError}>
              {error}
            </Banner>
          </div>
        </div>
      )}

      <div
        style={{
          padding: 'var(--spacing-md) var(--spacing-lg) var(--spacing-lg)',
          backgroundColor: 'var(--bg-primary)',
          flexShrink: 0,
        }}
      >
        <div style={{ maxWidth: 880, marginInline: 'auto', width: '100%' }}>
          <ChatComposer
            disabled={isSending}
            onSend={handleSend}
            onVoiceMode={openVoiceMode}
            {...(incognito
              ? { placeholder: 'Mode sécurisé Webba, rien n’est envoyé en ligne...' }
              : {})}
          />
        </div>
      </div>

      <VoiceModeOverlay
        open={voiceModeOpen}
        sessionId={sessionId}
        onClose={() => setVoiceModeOpen(false)}
      />
    </div>
  )
}
